import * as React from "react"

export type UserSuggestion = {
  id: number
  name: string
  email: string | null
  organization: string | null
  href: string
}

/**
 * Debounced suggestions from /admin/users/search.
 *
 * Shared by the user list and the change log's person filter, so the two ask
 * the same endpoint the same way. A raw fetch rather than Inertia's router:
 * typing must not navigate, and the page under the dropdown has its own
 * navigation to do.
 *
 * Every response carries the query it was for, and a stale one is dropped —
 * two keystrokes in flight can otherwise land out of order and leave the list
 * showing matches for a prefix the box no longer contains.
 */
export function useUserSearch(query: string, { delay = 250 }: { delay?: number } = {}) {
  const [suggestions, setSuggestions] = React.useState<UserSuggestion[]>([])
  const [loading, setLoading] = React.useState(false)

  React.useEffect(() => {
    const term = query.trim()

    if (term.length === 0) {
      setSuggestions([])
      setLoading(false)
      return
    }

    let current = true
    setLoading(true)

    const timer = window.setTimeout(() => {
      fetch(`/admin/users/search?q=${encodeURIComponent(term)}`, {
        headers: { Accept: "application/json" },
      })
        .then((response) => (response.ok ? response.json() : { results: [] }))
        .then((data) => {
          if (current) setSuggestions(data.results ?? [])
        })
        .catch(() => {
          // A failed lookup leaves the box usable — the list below is still
          // filtered by the same text, so there is nothing to report here.
          if (current) setSuggestions([])
        })
        .finally(() => {
          if (current) setLoading(false)
        })
    }, delay)

    return () => {
      current = false
      window.clearTimeout(timer)
    }
  }, [query, delay])

  return { suggestions, loading }
}
